import { isShaken } from "../cityCommand/cityVerbs";
import { capitalStands, inKingReach } from "../court/king";
import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals, hexKey } from "../hex";
import { workingCities } from "../land/cities";
import { canTakeIn, takeOnDraft } from "../land/territory";
import { nearestTo } from "../pick";
import { findHex, unitById } from "../run/board";
import { isCombatTick } from "../run/cadence";
import { kingOf } from "../run/pools";
import { seatOfUnit, seatUnit, soldierSeats } from "../run/seats";
import { sideOf } from "../run/sides";
import { payDeed, payDeeds } from "../soldier/bounty";
import { comradeBeside, enlistedWith, isAtWork, onDuty } from "../soldier/company";
import { leadsSquad } from "../soldier/lead";
import { leavesFree, maxHpOf, titleAttack } from "../soldier/valor";
import type { MutableHex, RunState, SeatId, Unit } from "../types/state";
import { isVeteran, settleCasualties, strikersOf, underFire } from "./combat";
import { nextStepOf, routeUnit, walledAgainst } from "./routes";
import { unitStats } from "./unit";

/**
 * Leaving and breaking (ADR-0013): breaking contact under parting blows, courage, and a broken unit's
 * flight home.
 */

/**
 * The hex a unit would step to if it broke contact now (ADR-0013 slice 1), or undefined: it
 * needs the tactics block, a destination, an enemy that can strike it, no step yet this tick,
 * and a next hex on its road that is free of every unit, enterable (own, or foreign and
 * wall-free) and in range of no enemy. `occupied` is the hexes held after earlier ids stepped
 * out in the same step; the renderer asks with the state as it stands.
 */
export function breakoutOf(
  state: RunState,
  unit: Unit,
  occupied: ReadonlySet<string> = new Set(state.units.map((held) => hexKey(held.coord))),
): AxialCoord | undefined {
  const tactics = state.rules.tactics;
  if (!tactics || !unit.destination || unit.movedTick === state.tick) {
    return undefined;
  }
  if (strikersOf(state, unit).length === 0) {
    return undefined;
  }
  const side = sideOf(state.rules, unit.owner);
  const next = nextStepOf(state.hexes, side, unit.coord, unit.destination);
  const hex = next === undefined ? undefined : findHex(state, next);
  if (!next || !hex || hex.razed || occupied.has(hexKey(next))) {
    return undefined;
  }
  if (walledAgainst(hex, side)) {
    return undefined;
  }
  return underFire(state, unit.owner, next) ? undefined : next;
}

/**
 * Step 4a: on the combat interval, before the blows, every unit that can break contact does,
 * in id order: the strongest enemy in range (attack, then id) lands `partingBlows` blows at its
 * full attack, no flank, hold, garrison or entrench arithmetic; a unit that survives steps to
 * the hex now (a Rider takes a wall-free foreign hex as it does on any step), and its hex is
 * free for a later id in the same step. A killed unit never moves. V1: a seat's soldier from
 * Captain leaves free, its title's attack is in its parting blow, and a foe its parting blow
 * kills is its kill while it lives at work, paid seat by seat in id order.
 */
export function breakContact(state: RunState): RunState {
  const tactics = state.rules.tactics;
  if (!tactics || !isCombatTick(state) || state.units.length === 0) {
    return state;
  }
  const occupied = new Set(state.units.map((unit) => hexKey(unit.coord)));
  let hexes: MutableHex[] | undefined;
  let changed = false;
  const kills = new Map<number, number>();
  const slain = new Map<SeatId, Unit[]>();
  const units = state.units.map((unit) => {
    const out = breakoutOf(state, unit, occupied);
    if (!out) {
      return unit;
    }
    changed = true;
    // A broken unit slips away free (ADR-0013 slice 3, A55): the parting blow is the price of an order.
    const [striker] = strikersOf(state, unit);
    const parting =
      striker && unit.brokenTick === undefined && !leavesFree(state, unit)
        ? (unitStats(state.rules, striker.kind).attack + titleAttack(state, striker)) *
          tactics.partingBlows
        : 0;
    const hp = unit.hp - parting;
    if (hp <= 0) {
      if (striker) {
        kills.set(striker.id, (kills.get(striker.id) ?? 0) + 1);
      }
      const seat = striker && seatOfUnit(state, striker.id);
      if (striker && seat && isAtWork(state, striker)) {
        slain.set(seat.id, [...(slain.get(seat.id) ?? []), unit]);
      }
      return { ...unit, hp: 0 };
    }
    occupied.delete(hexKey(unit.coord));
    occupied.add(hexKey(out));
    const there = findHex(state, out);
    if (
      there &&
      unitStats(state.rules, unit.kind).capturesOnArrival &&
      canTakeIn(hexes ?? state.hexes, state.rules, state.tick, there, unit.owner)
    ) {
      hexes = takeOnDraft(hexes, state, out, unit.owner);
    }
    const { destination, ...stepped } = { ...unit, hp, coord: out, movedTick: state.tick };
    return destination && !hexEquals(destination, out) ? { ...stepped, destination } : stepped;
  });
  if (!changed) {
    return state;
  }
  const settled: RunState = {
    ...state,
    hexes: hexes ?? state.hexes,
    units: settleCasualties(units, kills),
  };
  // V1: a kill pays only a striker that lives to be credited.
  let paid = settled;
  for (const seat of soldierSeats(state)) {
    const unit = seatUnit(state, seat);
    const foes = slain.get(seat.id);
    if (unit && foes && unitById(settled, unit.id)) {
      paid = foes.reduce(
        (next, foe) => payDeed(next, seat.id, isVeteran(state, foe) ? "veteranSlay" : "slay"),
        paid,
      );
    }
  }
  return paid;
}

/** A unit that broke and has not yet recovered (ADR-0013 slice 3): it takes no order. */
export function isBroken(state: RunState, unit: Unit): boolean {
  return unit.brokenTick !== undefined && state.rules.tactics !== null;
}

/**
 * Whether a unit has something to hold it in a fight (ADR-0013 slice 3): an own unit beside it,
 * its King's reach (not while he is shaken, CC1), a living commander it follows, its own command,
 * or an own city under it. Without a court there is no reach and no commander: only company and
 * the city hold it.
 */
export function hasCourage(state: RunState, unit: Unit): boolean {
  // VC: a seat's soldier while it leads has a commander's courage, and so do its men.
  if (leadsSquad(unit)) {
    return true;
  }
  // C2: the soldier that fell in has its squad's courage, as a member by `squadOf` has below.
  const seat = seatOfUnit(state, unit.id);
  if (seat && enlistedWith(state, seat)) {
    return true;
  }
  if (standsOnOwnCity(state, unit)) {
    return true;
  }
  const leader = unit.squadOf === undefined ? undefined : unitById(state, unit.squadOf);
  if (leader && leadsSquad(leader)) {
    return true;
  }
  if (
    kingOf(state, unit.owner) &&
    !isShaken(state, unit.owner) &&
    inKingReach(state, unit.owner, unit.coord)
  ) {
    return true;
  }
  return state.units.some(
    (other) =>
      other.owner === unit.owner &&
      other.id !== unit.id &&
      hexDistance(other.coord, unit.coord) === 1,
  );
}

/**
 * Whether an unbroken unit breaks at this combat step (ADR-0013 slice 3): at or under
 * `breakShare` percent of its full HP (its title's, V1), with an enemy that can strike it and no
 * courage. `breakUnits` breaks exactly these; `fight` reads it on the state `breakUnits` reads.
 */
export function breaksNow(state: RunState, unit: Unit): boolean {
  const tactics = state.rules.tactics;
  return (
    tactics !== null &&
    tactics.breakShare > 0 &&
    isCombatTick(state) &&
    unit.brokenTick === undefined &&
    unit.hp * 100 <= tactics.breakShare * maxHpOf(state, unit) &&
    strikersOf(state, unit).length > 0 &&
    !hasCourage(state, unit)
  );
}

/** The unit stands on a city of its own, built or not: there it has courage, and a broken one recovers. */
function standsOnOwnCity(state: RunState, unit: Unit): boolean {
  const here = findHex(state, unit.coord);
  return here !== undefined && here.owner === unit.owner && here.structure === "city";
}

/**
 * Where a broken unit runs (ADR-0013 slice 3): the nearest own working city it has a road to
 * (ties by insertion order), else the capital while it stands, else nowhere: it stands.
 */
function homeOf(state: RunState, unit: Unit): AxialCoord | undefined {
  const city = nearestTo(
    workingCities(state, unit.owner).filter((hex) => routeUnit(state, unit, hex.coord)),
    unit.coord,
  );
  if (city) {
    return city.coord;
  }
  const king = kingOf(state, unit.owner);
  return king && capitalStands(state, unit.owner) && routeUnit(state, unit, king.capital)
    ? king.capital
    : undefined;
}

/**
 * Step 4b (ADR-0013 slice 3), every tick after the blows: a broken unit past
 * `brokenTick + breakTicks`, or standing on an own city, recovers; then, on the combat
 * interval, every unit `breaksNow` reads (at or under `breakShare` percent of its HP with an
 * enemy that can strike it and no courage) breaks: it drops its order and its errand, walks home, and takes
 * the first hex of its road at once when that hex is free and enterable (A55: as specified,
 * with the parting blow and the wait for the march, 82 of 110 broken units died within 24 ticks).
 */
export function breakUnits(state: RunState): RunState {
  const tactics = state.rules.tactics;
  if (!tactics || tactics.breakShare <= 0 || state.units.length === 0) {
    return state;
  }
  const combatStep = isCombatTick(state);
  const occupied = new Set(state.units.map((unit) => hexKey(unit.coord)));
  let hexes: MutableHex[] | undefined;
  let changed = false;
  // C2, steadied: on the combat step a comrade beside a broken unit ends its flight.
  const steadies = state.rules.company !== null && combatStep;
  const comers = soldierSeats(state).map((seat) => ({ seat, comes: onDuty(state, seat) }));
  const steadiedBy = new Map<SeatId, number>();
  const units = state.units.map((unit) => {
    if (unit.brokenTick !== undefined) {
      const running =
        !standsOnOwnCity(state, unit) && state.tick < unit.brokenTick + tactics.breakTicks;
      if (running && !(steadies && comradeBeside(state, unit))) {
        return unit;
      }
      // The renown is for the one who came: beside it now, and stepped there after it broke.
      for (const { seat, comes } of comers) {
        if (
          running &&
          comes &&
          comes.id !== unit.id &&
          comes.owner === unit.owner &&
          comes.movedTick > unit.brokenTick &&
          hexDistance(comes.coord, unit.coord) === 1
        ) {
          steadiedBy.set(seat.id, (steadiedBy.get(seat.id) ?? 0) + 1);
        }
      }
      changed = true;
      return without(unit, "brokenTick");
    }
    if (!breaksNow(state, unit)) {
      return unit;
    }
    changed = true;
    // M2: what it carried is dropped where it broke, and lost; CC5: its work ends.
    const bare = without(unit, "destination", "governs", "carry", "works");
    const home = homeOf(state, unit);
    const broken: Unit = { ...bare, brokenTick: state.tick, orderedTick: state.tick };
    if (!home) {
      return broken;
    }
    const next = routeUnit(state, unit, home)?.[1];
    const there = next === undefined ? undefined : findHex(state, next);
    if (
      !next ||
      !there ||
      there.razed ||
      occupied.has(hexKey(next)) ||
      walledAgainst(there, sideOf(state.rules, unit.owner))
    ) {
      return { ...broken, destination: home };
    }
    occupied.delete(hexKey(unit.coord));
    occupied.add(hexKey(next));
    if (
      unitStats(state.rules, unit.kind).capturesOnArrival &&
      canTakeIn(hexes ?? state.hexes, state.rules, state.tick, there, unit.owner)
    ) {
      hexes = takeOnDraft(hexes, state, next, unit.owner);
    }
    const stepped = { ...broken, coord: next, movedTick: state.tick };
    return hexEquals(next, home) ? stepped : { ...stepped, destination: home };
  });
  const settled: RunState = changed ? { ...state, hexes: hexes ?? state.hexes, units } : state;
  let paid = settled;
  for (const { seat } of comers) {
    paid = payDeeds(paid, seat.id, "steadied", steadiedBy.get(seat.id) ?? 0);
  }
  return paid;
}
